import React, { useEffect, useState } from 'react';
import { Box, TextField, Typography } from '@mui/material';
import DatePicker from 'react-date-picker';
import { isEmpty } from 'lodash';
import { IExpertBrand, IClient } from './constants';
import { SelectPanel } from './SelectPanel';
import { IconPlus } from '../../../../utils/icons';
import styles from './datepicker.module.css';
import FormRow from './row';

interface Props {
  brandList: Array<IExpertBrand>;
  activity?: IClient;
  onChange: (profession: IClient) => void;
}

export default function SingleProfessionInput({
  brandList,
  activity,
  onChange,
}: Props) {
  const [brand, setBrand] = useState<IExpertBrand>();
  const [date, setDate] = useState<number>();
  const [notes, setNotes] = useState<string>();
  const [showInputPanel, setShowInputPanel] = useState(true);

  const calendarClassName = styles['date-picker-calendar-container'];

  useEffect(() => {
    if (activity?.date) setDate(Number(activity?.date));
    setNotes(activity?.notes);
    if (activity?.brandId && activity?.brand?.name)
      setBrand({ value: activity?.brandId, brand: activity.brand });
  }, [activity]);

  const onSubmit = () => {
    const output = {} as IClient;
    if (brand) {
      output.brandId = brand.value;
      output.brand = brand.brand;
    }
    if (date) output.date = date.toString();
    if (notes) output.notes = notes;
    if (!isEmpty(output)) onChange?.({ ...output });
    setBrand(undefined);
    setDate(undefined);
    setNotes(undefined);
    setShowInputPanel(false);
    setTimeout(() => setShowInputPanel(true), 10);
  };

  if (!showInputPanel) return null;

  return (
    <Box className="flex gap-3 sm:gap-4 items-stretch sm:items-end justify-between p-3 sm:p-4 border border-border-token rounded-lg w-full flex-col sm:flex-row">
      <Box className="flex gap-2 w-full min-w-0 flex-col">
        <FormRow count={2}>
          <Box className="flex gap-2 flex-col w-full">
            <Typography variant="body1" color="var(--text)">
              Brand
            </Typography>
            <SelectPanel
              value={brand}
              onChange={setBrand}
              placeholder="Select brand"
              objectOptions={brandList.map((e) => ({
                label: e.brand.name,
                value: e,
              }))}
            />
          </Box>
          <Box className="flex gap-2 flex-col w-full">
            <Typography variant="body1" color="var(--text)">
              Date last worked
            </Typography>
            <DatePicker
              format="MM/dd/yyyy"
              value={date ? new Date(date) : null}
              className="h-[40px] rounded-xl bg-surface-2"
              calendarClassName={calendarClassName}
              onChange={(value: Date) => {
                setDate(value?.valueOf());
              }}
            />
          </Box>
        </FormRow>
        <FormRow>
          <Box className="flex gap-2 flex-col w-full">
            <Typography variant="body1" color="var(--text)">
              Notes
            </Typography>
            <TextField
              value={notes}
              onChange={(e => setNotes(e.target.value))}
              size="small"
              multiline
              className="bg-surface-2 rounded-xl"
              placeholder="Notes"
              sx={{
                '& fieldset': { border: 'none' },
                '& .Mui-focused': {
                  border: '1px solid var(--accent)',
                  backgroundColor: 'var(--surface)',
                },
              }}
              InputProps={{
                className: 'border border-transparent hover:border-text-2',
              }}
            />
          </Box>
        </FormRow>
      </Box>
      <Box className="flex gap-2 items-end justify-end p-1 self-end sm:self-auto">
        <Box
          className="flex items-center justify-center bg-accent rounded-lg cursor-pointer hover:bg-[#8e682c]"
          onClick={onSubmit}
        >
          <IconPlus stroke="#fff" width={36} height={36} />
        </Box>
      </Box>
    </Box>
  );
}
